<!-- Collection Dock - Expanded View -->
@if (dockService.isVisible() && !dockService.isCollapsed()) {
  <div
    class="collection-dock"
    [class.drag-over]="dockService.isDragOver()"
    [class.loading]="dockService.isLoading()">

    <!-- Header -->
    <div class="dock-header">
      <!-- Collection Selector -->
      <div class="collection-selector focus-ring" role="button" tabindex="0" aria-haspopup="menu" appLabelDescribedBy
           (click)="collectionMenu.toggle($event)" (keydown.enter)="collectionMenu.toggle($event)">
        <fa-duotone-icon [icon]="faLayerGroup" class="collection-icon"></fa-duotone-icon>
        @if (dockService.defaultLoadFailed()) {
          <!-- The collections read failed: the header says so — never "Select a collection" over a list that never landed. -->
          <span class="collection-name select-prompt">{{ 'collections.dock.loadFailed' | translate }}</span>
        } @else if (dockService.hasActiveCollection()) {
          <span class="collection-name"><app-operational-label [value]="dockService.activeCollection()?.title" [fallback]="'collections.untitled' | translate" [nested]="true" /></span>
          <!-- The count is a claim about the collection — silent while its items read failed; a plural family, never a
               number glued to a flat noun ("1 items", an undeclined Czech "položek"). -->
          @if (!dockService.itemsLoadFailed()) {
            <span class="item-count">{{ dockService.itemCount() | l10nPlural: 'common.itemsCount' }}</span>
          }
        } @else {
          <span class="collection-name select-prompt">{{ 'collections.dock.selectCollection' | translate }}</span>
        }
        <fa-duotone-icon [icon]="faChevronDown" class="chevron-icon"></fa-duotone-icon>
      </div>

      <!-- PrimeNG Menu for collection switching -->
      <p-menu
        #collectionMenu
        [model]="collectionMenuItems()"
        [popup]="true"
        appendTo="body"
        styleClass="collection-dropdown-menu">
      </p-menu>

      <!-- Spacer to push actions to the right -->
      <div class="dock-spacer"></div>

      <!-- Actions (always visible when collection selected) -->
      @if (dockService.hasActiveCollection()) {
        <div class="dock-actions">
          <p-button
            [text]="true"
            [rounded]="true"
            (click)="openInCollections()"
            [pTooltip]="'collections.dock.openInCollections' | translate"
            tooltipPosition="top">
            <fa-duotone-icon [icon]="faArrowUpRightFromSquare"></fa-duotone-icon>
          </p-button>
          <!-- Share and download are a member's doors: sharing grants access, and a collection's download keys on admission (it carries no content role). -->
          @if (isMember()) {
          <p-button
            [text]="true"
            [rounded]="true"
            (click)="shareCollection()"
            [pTooltip]="'collections.dock.share' | translate"
            tooltipPosition="top">
            <fa-duotone-icon [icon]="faShareNodes"></fa-duotone-icon>
          </p-button>
          <p-button
            [text]="true"
            [rounded]="true"
            [loading]="collectionsService.downloading()"
            [disabled]="collectionsService.downloading()"
            (click)="downloadCollection()"
            [pTooltip]="'collections.dock.download' | translate"
            tooltipPosition="top">
            <fa-duotone-icon [icon]="faDownload"></fa-duotone-icon>
          </p-button>
          }
          @if (!dockService.isEmpty()) {
            <p-button
              [text]="true"
              [rounded]="true"
              styleClass="dock-clear-btn"
              (click)="clearCollection()"
              [pTooltip]="'collections.dock.clear' | translate"
              tooltipPosition="top">
              <fa-duotone-icon [icon]="faTrash"></fa-duotone-icon>
            </p-button>
          }
        </div>
      }

      <!-- Collapse button -->
      <p-button
        [text]="true"
        [rounded]="true"
        (click)="toggleCollapsed()"
        [pTooltip]="'collections.dock.collapse' | translate"
        tooltipPosition="top"
        styleClass="dock-collapse-btn">
        <fa-duotone-icon [icon]="faMinus"></fa-duotone-icon>
      </p-button>
    </div>

    <!-- Items Strip (only when collection selected) -->
    @if (dockService.hasActiveCollection()) {
      <div class="dock-items-wrapper">
        <!-- Scroll left button -->
        @if (canScrollLeft()) {
          <button class="scroll-btn scroll-left" (click)="scrollLeft()" type="button">
            <fa-duotone-icon [icon]="faChevronLeft"></fa-duotone-icon>
          </button>
        }

        <!-- Items container -->
        <div class="dock-items">
          @if (dockService.defaultLoadFailed()) {
            <!-- The member-collections read failed: the face owns the dock, with the same read as its Try again. -->
            <app-load-failed [compact]="true"
                             [message]="'collections.dock.loadFailed' | translate"
                             [retrying]="dockService.isLoading()"
                             (retry)="dockService.retryDefault()"/>
          } @else if (dockService.itemsLoadFailed()) {
            <!-- A failed items read is never "empty": the face owns the moment, with the same read as its
                 Try again, and stays (button busy) until that read lands. -->
            <app-load-failed [compact]="true"
                             [message]="'collections.dock.itemsLoadFailed' | translate"
                             [retrying]="dockService.itemsReloading()"
                             (retry)="dockService.retryItems()"/>
          } @else if (dockService.isEmpty()) {
            <!-- Empty state. The drag invitation renders only where the member may ADD to the active
                 collection: the dock's hover refuses a drop on a view-only collection, so inviting one
                 there would name a door the member does not have (the review over the follow-on wave,
                 2026-09-14). A view-only empty collection shows the empty box alone. -->
            <div class="dock-empty">
              @if (dockService.canAddItems()) {
                <span class="empty-hint">
                  {{ 'collections.dock.dragHint' | translate }}
                </span>
              }
            </div>
          } @else {
            <!-- Collection items -->
            @for (collectionItem of dockService.items(); track collectionItem.itemId) {
              <app-collection-dock-item
                [collectionItem]="collectionItem"
                [canRemove]="dockService.canAddItems()"
                (remove)="onRemoveItem($event)"
                (dblclick)="onItemDoubleClick(collectionItem)">
              </app-collection-dock-item>
            }
          }
        </div>

        <!-- Scroll right button -->
        @if (canScrollRight()) {
          <button class="scroll-btn scroll-right" (click)="scrollRight()" type="button">
            <fa-duotone-icon [icon]="faChevronRight"></fa-duotone-icon>
          </button>
        }
      </div>
    }

    <!-- Drop zone indicator -->
    @if (dockService.isDragOver() && dockService.hasActiveCollection()) {
      <div class="drop-indicator">
        <fa-duotone-icon [icon]="faPlus"></fa-duotone-icon>
        <span>{{ 'collections.dock.dropToAdd' | translate }}</span>
      </div>
    }
  </div>
}

<!-- Collection Dock - Collapsed View (Pill) -->
@if (dockService.isVisible() && dockService.isCollapsed()) {
  <div
    class="collection-dock-pill focus-ring"
    role="button" tabindex="0" appLabelDescribedBy
    (click)="expandDock()" (keydown.enter)="expandDock()"
    [class.drag-over]="dockService.isDragOver()">
    <fa-duotone-icon [icon]="faLayerGroup" class="pill-icon"></fa-duotone-icon>
    @if (dockService.defaultLoadFailed()) {
      <!-- The collections read failed: the pill says so; expanding shows the face with its Try again. -->
      <span class="pill-name select-prompt">{{ 'collections.dock.loadFailed' | translate }}</span>
    } @else if (dockService.hasActiveCollection()) {
      <span class="pill-name"><app-operational-label [value]="dockService.activeCollection()?.title" [fallback]="'collections.untitled' | translate" [nested]="true" /></span>
      <!-- The count is a claim about the collection — silent while the items read failed, like the header's cell. -->
      @if (!dockService.itemsLoadFailed()) {
        <span class="count-badge count-badge--primary">{{ formatCount(dockService.itemCount()) }}</span>
      }
    } @else {
      <span class="pill-name select-prompt">{{ 'collections.dock.selectCollection' | translate }}</span>
    }
  </div>
}

<!-- Confirmation Dialog -->
<p-confirmDialog></p-confirmDialog>
